import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import { JOURS_LONGS, MOIS } from "@/lib/dates";

export default function FormationBookingDialog({ open, onOpenChange, session, onBooked }) {
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", entreprise: "", entreprise_code: "", message: "" });
  const [loading, setLoading] = useState(false);

  if (!session) return null;
  const d = new Date(session.date);
  const dateLabel = `${JOURS_LONGS[d.getDay()]} ${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()} à ${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/formations/book", { session_id: session.id, ...form });
      toast.success("Réservation confirmée ! Vous recevrez un rappel par email.");
      onBooked?.();
      onOpenChange?.(false);
      setForm({ nom: "", prenom: "", email: "", telephone: "", entreprise: "", entreprise_code: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Réserver une place</DialogTitle>
          <DialogDescription>
            <div className="mt-2 p-3 border border-[#002FA7] bg-[#F8F9FA]">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-1">Session sélectionnée</div>
              <div className="font-display font-bold text-[#0A0A0A]">{session.title}</div>
              <div className="text-xs text-[#495057] mt-1">{dateLabel} · {session.duree} · {session.mode}</div>
              <div className="text-xs text-[#059669] font-semibold mt-1">
                <CheckCircle2 className="inline h-3 w-3 mr-1" /> {session.places_restantes} place{session.places_restantes > 1 ? "s" : ""} restante{session.places_restantes > 1 ? "s" : ""} · Incluse
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
          <div className="grid grid-cols-2 gap-3">
            <F label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} testid="book-nom" required />
            <F label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} testid="book-prenom" required />
            <F label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="book-email" required />
            <F label="Téléphone" type="tel" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} testid="book-telephone" required />
            <F label="Entreprise" value={form.entreprise} onChange={(v) => setForm({ ...form, entreprise: v })} testid="book-entreprise" />
            <F label="Code PAG-ENT (si abonné)" value={form.entreprise_code} onChange={(v) => setForm({ ...form, entreprise_code: v })} testid="book-code" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Message (optionnel)</Label>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} data-testid="book-message" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="book-cancel">Annuler</Button>
            <Button type="submit" disabled={loading} data-testid="book-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
              {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi…</>) : "Confirmer ma réservation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}{required && <span className="text-[#E60000]"> *</span>}</Label>
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-10 border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" required={required} />
    </div>
  );
}
